/**
 * The two sentences at the top of the Overview screen, and its banners.
 *
 * Out of `Overview.tsx` because `node --test` refuses a `.tsx`: anything left in
 * the component is beyond the reach of anything that runs, and these strings are
 * what an operator reads before lending an ad account.
 *
 * `LoadState` is written out rather than imported from `useLoad.ts`. The type
 * would be the tidier import, and it would drag `api.ts` in behind it, which
 * reads `import.meta.env` through `config.ts` — and then this module would
 * typecheck, build, and fail to load under the runner.
 *
 * Nothing here decides anything. It states what was counted, and its one duty is
 * to never state more than was counted. It says nothing about an account's
 * spending limit or spend except `LIMITS_NOT_SHOWN`: this screen shows neither,
 * so any other sentence about them would be said about every account.
 *
 * A file of the same shape lives in twads (`eea3b0a`, 2026-10-20): the same
 * `overviewCopy`, headline, subhead and banners. What differs: the two platform
 * constants below and the comments about them are each console's own; and twads
 * also carries everything about an account's funding instruments (a read limit,
 * `readsInstruments`, `instrumentLines`) with no `LIMITS_NOT_SHOWN` at all, since
 * its cards show X's figures. Compare before carrying either way.
 */

/** The platform, as an operator names it. */
const PLATFORM = "TikTok";

/**
 * Why no limit is shown. TikTok's advertiser/info returns no spending cap
 * (`docs/API-CONTRACT.md`, fields read off the Marketing API portal 2026-09-14).
 * Until 2026-10-20 this sentence said the console reads nothing else about
 * budgets. It has read campaign budgets since #33 — `GET /api/v1/campaigns`,
 * called by the Campaigns screen on every load and shown in its Budget column —
 * and ad group budgets since #109 (`GET /api/v1/adgroups`, under the same
 * screen), and shows no account limit from them on any screen. The sentence says
 * that, and names where they are read.
 */
const LIMITS_NOT_SHOWN =
	"Spending limits are not shown here: TikTok's advertiser/info reports none. This console reads campaign and ad group budgets on the Campaigns screen, and shows no account limit from them yet.";

export type LoadState = "loading" | "ready" | "failed";

/** One banner, ready to render. The screen decides nothing about it. */
export interface OverviewBanner {
	kind: "warning" | "danger";
	title: string;
	body: string;
}

/**
 * Everything the Overview screen says. No counts and no flags leave this
 * module — only finished sentences and a list of banners to render in the order
 * given.
 */
export interface OverviewCopy {
	headline: string;
	subhead: string;
	banners: OverviewBanner[];
}

/**
 * One call, and the screen renders what comes back.
 *
 * `count` is the caller's share of the accounts; `truncated` is whether the read
 * behind it was cut off. Both can be true at once — a tenant whose grants sit
 * past the ceiling sees none of their own.
 */
export function overviewCopy(
	state: LoadState,
	count: number,
	truncated: boolean,
	/**
	 * When `state` is failed: the status the console answered with, or null when nothing
	 * answered (lib/api-error.ts). Decides the headline — a 403 is not an outage.
	 */
	failedStatus: number | null = null,
	/**
	 * When `state` is failed: the console's own word for it (`Load.why`, the `error` of its
	 * JSON), or null. A 503 is the sign-in check only when this is `auth_unavailable`.
	 */
	failedWhy: string | null = null,
): OverviewCopy {
	const banners: OverviewBanner[] = [];
	/*
	 * Only on a finished read. The banner says the numbers on screen are floors of
	 * a read that was cut off; while loading, or after a failure, there is no read
	 * and no numbers, so a floor there would be a claim about nothing. The screen
	 * passes `ready && truncated` today, and this holds without relying on it.
	 */
	if (state === "ready" && truncated)
		banners.push({
			kind: "warning",
			title: "These numbers are floors, not totals.",
			body: "More accounts exist than one read returns, so the count above and the cards below cover the first 1000 only. Nothing is missing from the database.",
		});
	return {
		headline: headline(state, count, truncated, failedStatus, failedWhy),
		subhead: subhead(state, count, truncated, failedStatus, failedWhy),
		banners,
	};
}

/*
 * 🔴 The failed headline goes by the status. It used to say «The console database is
 * unreachable.» on every failure, a 403 included. On GET /tiktok-accounts a 503 is the
 * kit's `auth_unavailable` (middleware/auth.js): the sign-in check, not the store. The
 * route reads only the mirror, so `tiktok_unauthorized` never reaches it, and it answers
 * 500 for a database it cannot reach (`failure()` in routes/v1.ts) — a database sentence
 * belongs on the Overview only once that route answers 503 for the kit's
 * StoreUnavailableError. A 403 is a refusal; no answer at all is the console.
 *
 * The sign-in sentence goes by the kit's word, not the status alone: console-kit 0.4.2
 * middleware/auth.js:70 answers `{ error: "auth_unavailable" }` with 503, and a 503 with
 * any other body — the kit's `store_unavailable`, or a page that is not the console's
 * JSON — names its status like any other fault.
 */
const AUTH_UNAVAILABLE = "auth_unavailable";

const signInCheck = (status: number | null, why: string | null) =>
	status === 503 && why === AUTH_UNAVAILABLE;

function headline(
	state: LoadState,
	count: number,
	truncated: boolean,
	failedStatus: number | null,
	failedWhy: string | null,
): string {
	if (state === "loading") return "Loading your accounts…";
	if (state === "failed") {
		if (signInCheck(failedStatus, failedWhy)) return "The console could not check this sign-in.";
		if (failedStatus === 403) return "This sign-in may not read the ad accounts.";
		if (failedStatus === null) return "The console did not answer.";
		return `The ad accounts could not be read: the console answered ${failedStatus}.`;
	}
	// "At least 0" is not a sentence.
	if (truncated)
		return count === 0
			? "No ad accounts among the ones we could read."
			: `At least ${count} ad accounts under your control.`;
	if (count === 0) return "No ad accounts yet.";
	return count === 1
		? "One ad account under your control."
		: `${count} ad accounts under your control.`;
}

function subhead(
	state: LoadState,
	count: number,
	truncated: boolean,
	failedStatus: number | null,
	failedWhy: string | null,
): string {
	if (state === "loading") return "Reading the accounts you have been granted.";
	if (state === "failed") {
		if (signInCheck(failedStatus, failedWhy))
			return "Its sign-in check answered 503, which is not a refusal: nothing lists until the check is back. The console's reason is below.";
		if (failedStatus === 403)
			return "The console refused the read (403). What this sign-in may hold is set on Access.";
		if (failedStatus === null)
			return "Nothing came back — the network, or the console itself. The reason is below.";
		return "Nothing lists until the read succeeds. The console's reason is below.";
	}
	if (truncated)
		// The caller's share can be empty while the read was still cut off. Saying
		// "at least 0" there would be a sentence about nothing.
		return count === 0
			? "The read stopped at 1000 rows before reaching any of yours, which is not the same as having none."
			: "Not all of them were read: one read stops at 1000 rows.";
	if (count === 0)
		return `Sync the ad accounts from ${PLATFORM} on the Ad accounts page, then grant them out on Access.`;
	/*
	 * Said rather than left out. An operator who has seen a limit on another
	 * console would read silence as "no limit" — which is a claim about the
	 * account, not about this console.
	 */
	return LIMITS_NOT_SHOWN;
}
